import React from 'react';

import { Remove as RemoveIcon } from '@mui/icons-material';
import { Box, Divider, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { DateTime, Interval } from 'luxon';

import { DateFormatPatterns } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatMoney } from '@clearverify/utils';

import {
  DashboardCvRevenueByVerificationTypeItemInfoFragment,
  DashboardVerificationRequestGroup,
} from 'src/api';
import { ChartBase, ChartOptions } from 'src/components/chart';
import { ButtonDatePicker } from 'src/components/common';
import { DashboardPalette } from 'src/constants/dashboard';
import { useDashboardCVAdminData, useDashboardPrintView } from 'src/hooks/dashboard';
import { snapToUtcMonthEnd, snapToUtcMonthStart } from 'src/utils/dashboard';

import { DashboardChartWrapper } from '../DashboardChartWrapper';

import { DashboardClearVerifyRevenueDimensions, VerificationRequestGroup } from './types';

export type DashboardClearVerifyRevenueByVerificationTypeProps = {
  revenueByVerificationTypeItems: DashboardCvRevenueByVerificationTypeItemInfoFragment[];
  minDate?: DateTime;
  maxDate?: DateTime;
  loading?: boolean;
};

export const DashboardClearVerifyRevenueByVerificationType: React.FC<
  DashboardClearVerifyRevenueByVerificationTypeProps
> = ({ revenueByVerificationTypeItems, minDate = START_OF_2023, maxDate = NOW, loading }) => {
  const { isPrintView } = useDashboardPrintView();
  const {
    revenueByVerificationTypeStartDate: startDate,
    revenueByVerificationTypeEndDate: endDate,

    setRevenueByVerificationTypeStartDate: onStartDateChange,
    setRevenueByVerificationTypeEndDate: onEndDateChange,
  } = useDashboardCVAdminData();

  const handleStartDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onStartDateChange(snapToUtcMonthStart(date)),
    [onStartDateChange],
  );

  const handleEndDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onEndDateChange(snapToUtcMonthEnd(date)),
    [onEndDateChange],
  );

  const interval = startDate && endDate ? Interval.fromDateTimes(startDate, endDate) : undefined;

  const months = interval
    ?.splitBy({ months: 1 })
    .map(interval => interval.start?.toFormat(DateFormatPatterns.fullMonthYear))
    .filter(isNotNil);

  const isDatasetEmpty = !revenueByVerificationTypeItems.length && !loading;

  const options = React.useMemo(
    (): ChartOptions => ({
      grid: { top: 10, right: 40, bottom: 55, left: 40, containLabel: true },
      animationDuration: isPrintView ? 0 : undefined,
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
          formatter: value => formatMoney(value.valueOf()),
        },
      },
      legend: {
        show: true,
        bottom: 0,
        textStyle: {
          fontSize: 13,
        },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: value => formatMoney(Number(value?.valueOf())),
      },
      series: [
        {
          name: VerificationRequestGroup.verificationOfEmployment,
          datasetId: VerificationRequestGroup.verificationOfEmployment,
          type: 'bar',
          encode: {
            x: DashboardClearVerifyRevenueDimensions.month,
            y: DashboardClearVerifyRevenueDimensions.chargeAmount,
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.grey,
        },
        {
          name: VerificationRequestGroup.verificationOfIncome,
          datasetId: VerificationRequestGroup.verificationOfIncome,
          type: 'bar',
          encode: {
            x: DashboardClearVerifyRevenueDimensions.month,
            y: DashboardClearVerifyRevenueDimensions.chargeAmount,
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.green400,
        },
        {
          name: VerificationRequestGroup.reverification,
          datasetId: VerificationRequestGroup.reverification,
          type: 'bar',
          encode: {
            x: DashboardClearVerifyRevenueDimensions.month,
            y: DashboardClearVerifyRevenueDimensions.chargeAmount,
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.blue200,
        },
      ],
      dataset: [
        {
          id: 'dataset_raw',
          dimensions: [
            DashboardClearVerifyRevenueDimensions.month,
            DashboardClearVerifyRevenueDimensions.chargeAmount,
            DashboardClearVerifyRevenueDimensions.type,
          ],
          source: revenueByVerificationTypeItems,
        },
        {
          id: VerificationRequestGroup.verificationOfEmployment,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: DashboardClearVerifyRevenueDimensions.type,
                '=': DashboardVerificationRequestGroup.VerificationOfEmployment,
              },
            },
          ],
        },
        {
          id: VerificationRequestGroup.verificationOfIncome,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: DashboardClearVerifyRevenueDimensions.type,
                '=': DashboardVerificationRequestGroup.VerificationOfIncome,
              },
            },
          ],
        },
        {
          id: VerificationRequestGroup.reverification,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: DashboardClearVerifyRevenueDimensions.type,
                '=': DashboardVerificationRequestGroup.Reverification,
              },
            },
          ],
        },
      ],
    }),
    [revenueByVerificationTypeItems, isPrintView, loading, months],
  );

  return (
    <Paper sx={{ py: 2.5, px: 3, borderRadius: 2, overflow: 'hidden' }} variant="outlined">
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">Clear Verify Revenue by Verification Type</Typography>
          <Typography whiteSpace="pre-line" variant="body1" color="text.secondary">
            Breakdown revenue by verification type on a monthly basis for the provided date range.
            {'\n'} Please note: It is important to understand that reverifications will show as a
            duplicate count. They are represented in the count of Verification of Income and
            Verification of Employment.
          </Typography>
        </Box>
        <Box height="4px">{loading ? <LinearProgress color="secondary" /> : <Divider />}</Box>

        <Stack direction="row" alignItems="center" spacing={1}>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate}
            maxDate={endDate || maxDate}
            value={startDate}
            onChange={handleStartDateChange}
            disabled={loading}
          />
          <Box display="flex" alignItems="center" fontSize="13px">
            <RemoveIcon fontSize="inherit" />
          </Box>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate}
            maxDate={maxDate}
            value={endDate}
            onChange={handleEndDateChange}
            disabled={loading}
          />
        </Stack>

        {Boolean(months?.length) && (
          <DashboardChartWrapper
            wrapperHeight={300}
            isDatasetEmpty={isDatasetEmpty}
            isPrintView={isPrintView}
          >
            <ChartBase option={options} style={{ height: 300 }} />
          </DashboardChartWrapper>
        )}
      </Stack>
    </Paper>
  );
};

const NOW = DateTime.utc();
const START_OF_2023 = DateTime.fromISO('2023-01-01T00:00:00.000', { zone: 'utc' }).startOf('year');

const LABEL_OPTIONS = {
  show: true,
  fontSize: '16px',
  fontWeight: 'bold' as const,
  formatter: (params: any) => formatMoney(params.data.chargeAmount),
  position: 'inside' as const,
  offset: [60, 0],
  rotate: 90,
  inside: false,
};
